import React, { useState } from 'react';
import { Button, Form, Input } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { PhoneInput } from '@sezenta/antd-schema-form-adapters';

interface ContactPerson {
  [key: string]: string; // Allows dynamic keys for the contact person object
}

interface FieldSchema {
  id: string;
  label: string;
  type: string;
  placeholder?: string;
  layout?: { xs: { span: number }; md: { span: number } };
  props?: { [key: string]: any };
  options?: {
    rules?: { required?: boolean; message?: string; validator?: string }[];
  };
}

interface ContactPersonsInputProps {
  value?: ContactPerson[];
  onChange?: (value: ContactPerson[]) => void;
  fields?: FieldSchema[];
}

const ContactPersonsInput: React.FC<ContactPersonsInputProps> = ({
  value = [],
  onChange,
  fields = [], // Default to an empty array
}) => {
  const [items, setItems] = useState<ContactPerson[]>(value);
  const [form] = Form.useForm(); // Ant Design Form instance

  const addItem = () => {
    if (!fields.length) {
      console.error('Fields are not defined or empty.');
      return;
    }

    const newItem: ContactPerson = fields.reduce((acc, field) => {
      acc[field.id] =
        field.type === 'phone' ? field.props?.defaultCountry || '' : '';
      return acc;
    }, {} as ContactPerson);

    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    onChange?.(updatedItems);
  };

  const updateItem = (index: number, fieldId: string, fieldValue: string) => {
    const updatedItems = [...items];
    updatedItems[index][fieldId] = fieldValue; // Update the specific field
    setItems(updatedItems);
    onChange?.(updatedItems);

    // Trigger validation on the updated field
    form.validateFields([`${index}-${fieldId}`]);
  };

  const removeItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index); // Remove the item at the given index
    setItems(updatedItems);
    onChange?.(updatedItems);
  };

  return (
    <Form form={form} layout="vertical">
      {items.map((item, index) => (
        <div
          key={index}
          style={{
            marginBottom: '16px',
            padding: '8px',
            border: '1px solid #ccc',
          }}
        >
          {fields.map((field) => (
            <Form.Item
              key={`${field.id}-${index}`}
              label={field.label}
              name={[index, field.id]}
              style={{ marginBottom: '8px' }}
              rules={[
                { required: true, message: `${field.label} is required` },
                field.type === 'email'
                  ? { type: 'email', message: 'Please enter a valid email' }
                  : {},
              ]}
              initialValue={item[field.id]}
            >
              {field.type === 'phone' ? (
                <PhoneInput
                  defaultCountry={field.props?.defaultCountry || 'LK'}
                  placeholder={field.props?.placeholder || 'Enter phone number'}
                  value={item[field.id] ?? ''} // Ensure value is always a string
                  onChange={(value) => updateItem(index, field.id, value ?? '')} // Ensure onChange receives a string
                  className="w-full"
                />
              ) : (
                <Input
                  type={field.type}
                  placeholder={field.placeholder}
                  value={item[field.id] || ''}
                  onChange={(e) => updateItem(index, field.id, e.target.value)}
                />
              )}
            </Form.Item>
          ))}
          <div className="flex justify-end">
            <Button
              danger
              onClick={() => removeItem(index)}
              style={{ marginTop: '8px' }}
            >
              <DeleteOutlined />
            </Button>
          </div>
        </div>
      ))}
      <Button type="dashed" onClick={addItem} style={{ marginTop: '16px' }}>
        <PlusOutlined />
        Add Contact Person
      </Button>
    </Form>
  );
};

export default ContactPersonsInput;
